<section
  class="py-12 sm:py-16 lg:py-24 border-b transition-colors"
  [ngClass]="theme === 'warm' ? 'bg-[#f5efe6] border-stone-200 text-stone-900' : (theme === 'bold' ? 'bg-slate-950 text-white border-blue-900/60' : 'bg-base-200/40 border-base-300')"
>
  <div class="container mx-auto px-4 sm:px-6 max-w-6xl">
    
    <!-- Section Subtitle & Badge -->
    <div class="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
      <div
        class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3"
        [ngClass]="theme === 'warm' ? 'bg-amber-500/15 text-amber-900 border border-amber-400/40' : (theme === 'bold' ? 'bg-red-600/20 text-red-300 border border-red-500/40' : 'bg-primary/10 text-primary')"
      >
        <i class="fa-solid fa-chalkboard-user"></i>
        <span>Academic Leadership</span>
      </div>
      <h2
        class="text-2xl sm:text-4xl font-extrabold tracking-tight break-words px-2"
        [ngClass]="theme === 'warm' ? 'text-stone-900' : (theme === 'bold' ? 'text-white' : 'text-base-content')"
      >
        Meet Your Instructor
      </h2>
    </div>

    <!-- Instructor Card -->
    <div
      class="daisy-card border overflow-hidden rounded-2xl sm:rounded-3xl transition-all"
      [ngClass]="theme === 'warm' ? 'bg-[#fffdfa] border-stone-200 shadow-xl' : (theme === 'bold' ? 'bg-slate-900 border-blue-800/50 shadow-2xl text-white' : 'bg-base-100 border-base-300 shadow-xl')"
    >
      <div class="grid grid-cols-1 lg:grid-cols-12 items-center">
        
        <!-- Left: Instructor Portrait -->
        <div
          class="lg:col-span-4 p-6 sm:p-10 lg:p-12 flex flex-col items-center text-center border-b lg:border-b-0 lg:border-r"
          [ngClass]="theme === 'warm' ? 'border-stone-200 bg-gradient-to-b from-amber-50/60 via-[#fffdfa] to-[#fffdfa]' : (theme === 'bold' ? 'border-blue-900/50 bg-gradient-to-b from-blue-950/40 via-slate-900 to-slate-900' : 'border-base-300 bg-gradient-to-b from-primary/5 via-base-100 to-base-100')"
        >
          <div class="relative mb-4 sm:mb-6">
            <div
              class="w-32 h-32 sm:w-44 sm:h-44 rounded-2xl sm:rounded-3xl overflow-hidden ring-4 shadow-lg"
              [ngClass]="theme === 'warm' ? 'ring-amber-500/30 bg-stone-100' : (theme === 'bold' ? 'ring-blue-500/40 bg-slate-800' : 'ring-primary/20 bg-base-200')"
            >
              <img
                [src]="avatarUrl"
                [alt]="name"
                class="w-full h-full object-cover object-center"
                (error)="$any($event.target).src = 'assets/images/default-avatar.png'"
              />
            </div>
            @if (experienceYears) {
              <div
                class="absolute -bottom-2.5 -right-1.5 text-[11px] sm:text-xs font-bold px-2.5 py-1 rounded-xl shadow-md flex items-center gap-1.5"
                [ngClass]="theme === 'warm' ? 'bg-amber-600 text-white' : (theme === 'bold' ? 'bg-red-600 text-white' : 'bg-primary text-primary-content')"
              >
                <i class="fa-solid fa-award"></i>
                <span>{{ experienceYears }}+ Years</span>
              </div>
            }
          </div>

          <h3
            class="text-lg sm:text-2xl font-bold"
            [ngClass]="theme === 'warm' ? 'text-stone-900' : (theme === 'bold' ? 'text-white' : 'text-base-content')"
          >{{ name }}</h3>
          <p
            class="text-xs sm:text-sm font-semibold mt-1"
            [ngClass]="theme === 'warm' ? 'text-amber-700' : (theme === 'bold' ? 'text-blue-400' : 'text-primary')"
          >{{ title }}</p>
        </div>

        <!-- Right: Bio & Key Highlights -->
        <div class="lg:col-span-8 p-5 sm:p-10 lg:p-12 space-y-5 sm:space-y-6">
          <div
            class="prose max-w-none text-sm sm:text-base leading-relaxed"
            [ngClass]="theme === 'warm' ? 'text-stone-700' : (theme === 'bold' ? 'text-slate-300' : 'text-base-content/80')"
          >
            <p>{{ bio }}</p>
          </div>

          <!-- Highlight Bullets -->
          <div
            class="pt-4 border-t"
            [ngClass]="theme === 'warm' ? 'border-stone-200' : (theme === 'bold' ? 'border-slate-800' : 'border-base-200')"
          >
            <h4
              class="text-xs font-bold uppercase tracking-wider mb-3"
              [ngClass]="theme === 'warm' ? 'text-stone-500' : (theme === 'bold' ? 'text-blue-300' : 'text-base-content/60')"
            >
              Teaching Highlights & Methodology
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              @for (point of highlights; track $index) {
                <div
                  class="flex items-start gap-3 p-3 rounded-2xl border"
                  [ngClass]="theme === 'warm' ? 'bg-stone-50 border-stone-200' : (theme === 'bold' ? 'bg-slate-800/80 border-slate-700' : 'bg-base-200/50 border-base-300/60')"
                >
                  <div
                    class="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    [ngClass]="theme === 'warm' ? 'bg-amber-100 text-amber-800' : (theme === 'bold' ? 'bg-red-500/20 text-red-400' : 'bg-success/15 text-success')"
                  >
                    <i class="fa-solid fa-check text-xs"></i>
                  </div>
                  <span
                    class="text-xs sm:text-sm font-medium leading-snug"
                    [ngClass]="theme === 'warm' ? 'text-stone-800' : (theme === 'bold' ? 'text-slate-200' : 'text-base-content')"
                  >
                    {{ point }}
                  </span>
                </div>
              }
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</section>
